import { isChart } from "./figure-chart";

/**
 * What a figure can be *done to* once it is open full screen.
 *
 * A figure in the flow of a question is an `<img>`, and an SVG served as an image
 * is a photograph: no pointer events reach it, no script inside it runs, not even
 * `:hover` applies. So none of this can live there — it lives in the full-screen
 * view, which is also where it belongs: "let me see the other side" and "let me
 * look closer" are things a learner does *after* deciding the drawing matters.
 *
 * Neither interaction needs the SVG inlined into the page. A pose is a different
 * URL (the generators take `rotation` and `angle`), and the loupe is the same URL
 * painted larger behind a round window. That keeps every figure-sizing rule in
 * `globals.css` — all of which are written against `img` — untouched.
 */

export type FigureInteraction = "peek" | "loupe" | "chart" | null;

/** Solids whose yaw genuinely changes which faces you can see. */
const TURNABLE = new Set(["cube", "rectangular-prism", "prism", "pyramid"]);

/**
 * Rotationally symmetric solids. Spinning them about the vertical axis changes
 * nothing a learner could see, so offering a drag that appears to do nothing
 * would be worse than offering none. Their tilt is adjustable instead — the top
 * ellipse opening up is the whole content of „ar iš viršaus matai apskritimą?".
 */
const TILTABLE = new Set(["cylinder", "cone"]);

const LOUPE = new Set([
  "protractor",
  "angle-on-protractor",
  "angle-protractor",
  "ruler",
  "pencil-with-ruler",
  "pencil-ruler",
  "graduated-cylinder",
  "thermometer",
]);

/** A net is a flat drawing of an unfolded solid, and turning it would answer
 *  „kuri išklotinė sulankstoma į kubą" — which is the question. */
const NEVER = new Set(["net-cube", "prism-net", "net-prism", "cube-net"]);

export type Axis = "rotation" | "angle";

export function figureType(src: string): string | null {
  const match = /\/svg\/([a-z0-9-]+)/i.exec(src);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Which parameter a drag moves — and each figure gets exactly one.
 *
 * A polyhedron turns and does not tilt: `angle` is the generators' isometric
 * skew rather than a camera elevation, so tilting a cube past about 50° folds it
 * into a vertical spindle and under 15° into a flat lid. Neither is a drawing of
 * a cube, and a control whose range is mostly nonsense is worse than none.
 */
export function axisFor(src: string): Axis | null {
  const type = figureType(src);
  if (!type || NEVER.has(type)) return null;
  if (TURNABLE.has(type)) return "rotation";
  if (TILTABLE.has(type)) return "angle";
  return null;
}

export function interactionFor(src: string): FigureInteraction {
  const type = figureType(src);
  if (!type || NEVER.has(type)) return null;
  if (axisFor(src)) return "peek";
  if (LOUPE.has(type)) return "loupe";
  if (isChart(src)) return "chart";
  return null;
}

/**
 * A peek, not a free turntable: the figure gives a little, then comes back.
 *
 * Bounded because the drawing in the question is the one the sentence was written
 * about — a learner who leaves a solid parked at 130° is reading a different
 * picture from the one being asked about. Coming back on its own is what makes
 * the bound feel like the figure's own springiness rather than a limit.
 */
export const PEEK_RANGE = 24;
/** Degrees between neighbouring poses. Each is a separate render, so this is the
 *  trade between smoothness and how many of them exist. */
export const PEEK_STEP = 1;
/** How long the return takes. Long enough to be a movement and not a cut. */
export const RETURN_MS = 520;
/** Degrees of turn per pixel dragged: the whole range in about 60px. */
export const DEG_PER_PX = 0.42;

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/** Every pose, in order, so they can be preloaded and painted up front. */
export function poseOffsets(): number[] {
  const list: number[] = [];
  for (let d = -PEEK_RANGE; d <= PEEK_RANGE; d += PEEK_STEP) list.push(d);
  return list;
}

/** The pose the question drew, which is what the figure returns to. */
export function baseAngle(src: string, axis: Axis): number {
  const url = new URL(src, window.location.origin);
  const raw = url.searchParams.get(axis);
  if (raw !== null && raw !== "" && !Number.isNaN(Number(raw))) return Number(raw);
  // The generators' own defaults: no yaw, and a 30° isometric skew.
  return axis === "rotation" ? 0 : 30;
}

/**
 * One pose. The canvas is pinned to a square so the drawing cannot slide as it
 * turns: a solid's silhouette is narrower at 45° than at 0°, and a figure sized
 * to its own content would jump sideways on every frame.
 */
export function poseSrc(src: string, axis: Axis, offset: number, canvas: number): string {
  const url = new URL(src, window.location.origin);
  url.searchParams.set(axis, String(round(baseAngle(src, axis) + offset)));
  url.searchParams.set("width", String(canvas));
  url.searchParams.set("height", String(canvas));
  return url.pathname + "?" + url.searchParams.toString();
}

/** Degrees a drag has reached, clamped to the peek's range. */
export function offsetFromDrag(from: number, dx: number, dy: number, axis: Axis): number {
  // Negated against the raw delta, because a drag is a hand on the near face of
  // the object, not a hand on the camera: dragging right has to carry the face
  // nearest you to the right, and pulling down has to bring the top into view.
  const moved = axis === "rotation" ? -dx : dy;
  return clamp(from + moved * DEG_PER_PX, -PEEK_RANGE, PEEK_RANGE);
}

/** The nearest pose that exists, since only whole steps are rendered. */
export function snap(offset: number): number {
  const stepped = Math.round(offset / PEEK_STEP) * PEEK_STEP;
  return clamp(stepped, -PEEK_RANGE, PEEK_RANGE);
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
